import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormDropDown from "../common/FormDropDown";
import FormInputCheckbox from "../common/FormInputCheckbox";
import UploadManager from "../../shared/UploadManager";
// import ConditionReport from "../../model/ConditionReport";
import e3dModel from "../../model/e3dModel";
import CHElement from "../../model/CHElement";
import Material from "../../model/Material";
import { UserSummaryOutDTO } from "../../model/User";
// import CHUsage from "../../model/CHUsage";
// import InspectionMode from "../../model/InspectionMode";
// import InspectionModeItem from "../../model/InspectionModeItem";
import ConservationState from "../../model/enums/ConservationState";
import UsageTypes from "../../model/enums/UsageTypes";
import FormRichTextEditor from "../common/FormRichTextEditor";
import FormInputDate from "../common/FormInputDate";
import DocumentState from "../../model/enums/DocumentState";
import PagedData from "../../model/PagedData";
import Annotation from "../../model/Annotation";
import _3DInstance from "../../model/_3DInstance";
import CHElementScale from "../../model/enums/CHElementScale";
import AdministrativeStructure from "../../model/AdministrativeStructure";
import { useRef, useLayoutEffect } from "react";
import Country from "../../model/Country";
import AppIcons from "../../utils/AppIcons";
import ConditionReportAddEdit_contributors from "./ConditionReportAddEdit_contributors";
import ConditionReportAddEdit_annotations from "./ConditionReportAddEdit_annotations";
import ConditionReportAddEdit_references from "./ConditionReportAddEdit_references";
// import CHUsageItem from "../../model/CHUsageItem";
import AnnexData from "../../model/AnnexData";
// import ConditionReportAddEdit_a from "./ConditionReportAddEdit_a";

import {
  ConditionReportBase,
  ConditionReportInDTO,
  ConditionReportOutDTO,
} from "../../model/ConditionReportBase";
import { CHUsage, CHUsageInDTO, CHUsageOutDTO } from "../../model/CHUsageBase";
import {
  InspectionMode,
  InspectionModeOutDTO,
} from "../../model/InspectionModeBase";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";

interface IConditionReportFormErrors {
  name: string;
  address: string;
  date: string;
  author: string;
  cultural_sphere: string;
  legal_status: string;
  conservation_history: string;
  reference_cartography_municipality: string;
  reference_cartography_cadastral_sheet: string;
  reference_cartography_cadastral_parcel: string;
  reference_cartography_other: string;
  parent_asset_name: string;
  specific_location: string;
  colocation_characteristics: string;
  description: string;
  techniques: string;
  object: string;
  subject: string;
  diagnosis_description: string;
  condition_recommendation: string;
  state_of_conservation: string;
  environmental_conditions_temperature: string;
  environmental_conditions_relative_humidity: string;
  environmental_conditions_current_weather_condition: string;
  environmental_conditions_past_week_weather_condition: string;
  survey_date: string;
  survey_responsible: string;
  report_references: string;
  status: DocumentState;
}

const defaultFormErrors: IConditionReportFormErrors = {
  name: "",
  address: "",
  date: "",
  author: "",
  cultural_sphere: "",
  legal_status: "",
  conservation_history: "",
  reference_cartography_municipality: "",
  reference_cartography_cadastral_sheet: "",
  reference_cartography_cadastral_parcel: "",
  reference_cartography_other: "",
  parent_asset_name: "",
  specific_location: "",
  colocation_characteristics: "",
  description: "",
  techniques: "",
  object: "",
  subject: "",
  diagnosis_description: "",
  condition_recommendation: "",
  state_of_conservation: "",
  environmental_conditions_temperature: "",
  environmental_conditions_relative_humidity: "",
  environmental_conditions_current_weather_condition: "",
  environmental_conditions_past_week_weather_condition: "",
  survey_date: "",
  survey_responsible: "",
  report_references: "",
  status: DocumentState.draft,
};

type TabKey = "tab1" | "tab2" | "tab3" | "tab4" | "tab5";

const TAB_LABELS: Record<TabKey, string> = {
  tab1: "General Information",
  tab2: "Description",
  tab3: "Diagnosis",
  tab4: "Survey Information",
  tab5: "Contributors",
};

const FIELD_TAB_MAP: Record<string, { tab: TabKey; label: string }> = {
  name: { tab: "tab1", label: "Condition Report Name" },
  address: { tab: "tab1", label: "Address" },
  specific_location: { tab: "tab1", label: "Specific Location" },
  colocation_characteristics: {
    tab: "tab1",
    label: "Colocation Characteristics",
  },
  date: { tab: "tab1", label: "Date" },
  author: { tab: "tab1", label: "Author" },
  cultural_sphere: { tab: "tab1", label: "Cultural Sphere" },
  legal_status: { tab: "tab1", label: "Legal Status" },
  reference_cartography_municipality: {
    tab: "tab1",
    label: "Ref. Cartography – Municipality",
  },
  reference_cartography_cadastral_sheet: {
    tab: "tab1",
    label: "Ref. Cartography – Cadastral Sheet",
  },
  reference_cartography_cadastral_parcel: {
    tab: "tab1",
    label: "Ref. Cartography – Cadastral Parcel",
  },
  reference_cartography_other: {
    tab: "tab1",
    label: "Ref. Cartography – Other",
  },
  conservation_history: { tab: "tab1", label: "Conservation History" },
  parent_asset_name: { tab: "tab1", label: "Parent Asset Name" },
  description: { tab: "tab2", label: "General Description" },
  object: { tab: "tab2", label: "Object" },
  subject: { tab: "tab2", label: "Subject" },
  techniques: { tab: "tab2", label: "Techniques" },
  diagnosis_description: { tab: "tab3", label: "Diagnosis Description" },
  state_of_conservation: { tab: "tab3", label: "State of Conservation" },
  environmental_conditions_temperature: { tab: "tab4", label: "Temperature" },
  environmental_conditions_relative_humidity: {
    tab: "tab4",
    label: "Relative Humidity",
  },
  environmental_conditions_current_weather_condition: {
    tab: "tab4",
    label: "Current Weather Condition",
  },
  environmental_conditions_past_week_weather_condition: {
    tab: "tab4",
    label: "Past Week Weather Condition",
  },
  survey_date: { tab: "tab4", label: "Survey Date" },
  survey_responsible: { tab: "tab4", label: "Survey Responsible" },
  condition_recommendation: { tab: "tab4", label: "Recommendations" },
  report_references: { tab: "tab4", label: "Report References" },
};

interface IApiErrorSummaryItem {
  tab: TabKey;
  tabLabel: string;
  fields: string[];
}

interface ConditionReportAddEditProps {
  editCopy?: boolean;
}

function ConditionReportAddEdit({ editCopy }: ConditionReportAddEditProps) {
  // read the ID from the url
  const { e3did, cid } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<ConditionReportOutDTO>(
    ConditionReportOutDTO.EmptyInstance(),
  );
  // set state for parent e3D model
  const [e3D, setE3D] = useState<e3dModel>();
  // set state for parent e3D model
  const [chelement, setChelement] = useState<CHElement>();
  const [id_chelement, setId_chelement] = useState<number>(-1);
  const [chelementScale, setChelementScale] = useState<CHElementScale>(
    CHElementScale.Object,
  );
  const [searchText, setSearchText] = useState<string>("");
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnotations, setSelectedAnnotations] = useState<Annotation[]>(
    [],
  );

  // the list of available CHUsages
  const [allCHUsages, setAllCHUsages] = useState<CHUsageOutDTO[]>([]);
  // the list of selected CHUsage IDs
  const [selectedCHUsages, setSelectedCHUsages] = useState<CHUsageOutDTO[]>([]);
  // the ID of the Other CHUsage
  const [otherCHUsage, setOtherCHUsage] = useState<CHUsageOutDTO | undefined>(
    undefined,
  );
  // the description of the Other CHUsage
  const [otherCHUsageDescription, setOtherCHUsageDescription] = useState("");

  // the list of available InspectionModes
  const [allInspectionModes, setAllInspectionModes] = useState<
    InspectionModeOutDTO[]
  >([]);
  // the list of selected InspectionMode IDs
  const [selectedInspectionModes, setSelectedInspectionModes] = useState<
    InspectionModeOutDTO[]
  >([]);
  // the ID of the Other InspectionMode
  const [otherInspectionMode, setOtherInspectionMode] = useState<
    InspectionModeOutDTO | undefined
  >(undefined);
  // the description of the Other InspectionMode
  const [otherInspectionModesDescription, setOtherInspectionModesDescription] =
    useState("");

  // const [allMaterials, setAllMaterials] = useState<Material[]>([]);
  const [selectedMaterialsIDs, setSelectedMaterialsIDs] = useState<number[]>(
    [],
  );

  // const [selectedAnnexDataIDs, setSelectedAnnexDataIDs] = useState<number[]>([]);
  const [selectedAnnexDataObjects, setSelectedAnnexDataObjects] = useState<
    AnnexData[]
  >([]);

  // the ID of the Other material
  // const [otherMaterialID, setOtherMaterialID] = useState<number | undefined>(undefined);
  // // the description of the Other material
  // const [otherMaterialDescription, setOtherMaterialDescription] = useState("");

  const [country, setCountry] = useState<string>("");
  const [province, setProvince] = useState<string>("");
  const [municipality, setMunicipaliy] = useState<string>("");

  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IConditionReportFormErrors>(defaultFormErrors);
  const [apiErrorSummary, setApiErrorSummary] = useState<
    IApiErrorSummaryItem[]
  >([]);

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [conservation_state, setConservationState] = useState<string>(
    ConservationState.Fair,
  );

  // list of users already in the shared list
  const [usersAlreadySharing, setUsersAlreadySharing] = useState<
    UserSummaryOutDTO[]
  >([]);

  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const [uploadManager] = useState(new UploadManager({ apiClient: apiClient }));

  const [activeTab, setActiveTab] = useState<
    "tab1" | "tab2" | "tab3" | "tab4" | "tab5"
  >("tab1");

  // Stage 1: Load e3D and condition report
  useEffect(() => {
    const loadData = async () => {
      try {
        if (e3did) {
          const e3d = await apiClient?.get<e3dModel>({
            url: APIEndPoints.E3DS.ONE(parseInt(e3did)),
          });
          setE3D(e3d);
          // console.log("Parent e3D:", e3d);
        }

        const pagedCHUsages = await apiClient?.get<PagedData<CHUsage[]>>({
          url: APIEndPoints.CHUSAGES.ALL,
        });
        setAllCHUsages(
          (pagedCHUsages.items ?? []).map((chu) =>
            CHUsageOutDTO.fromCHUsage(chu, ""),
          ),
        );
        // console.log("---------------Alllllll CHusages:")
        // console.log(pagedCHUsages);

        // get the ID of the Other chusage
        for (var i = 0; i < pagedCHUsages.items.length; i++) {
          if (pagedCHUsages.items[i].name?.trim() === "Other") {
            setOtherCHUsage(
              CHUsageOutDTO.fromCHUsage(pagedCHUsages.items[i]!, ""),
            );
            break;
          }
        }

        // console.log("All InspectionModes:");
        const pagedInspectonModes = await apiClient?.get<
          PagedData<InspectionMode[]>
        >({
          url: APIEndPoints.INSPECTION_MODES.ALL,
        });
        setAllInspectionModes(
          (pagedInspectonModes.items ?? []).map((im) =>
            InspectionModeOutDTO.fromInspectionMode(im, ""),
          ),
        );

        // console.log("All InspectionModes:");
        // console.log(pagedInspectonModes);

        // get the ID of the Other InspectionMode
        for (var i = 0; i < pagedInspectonModes.items.length; i++) {
          if (pagedInspectonModes.items[i].type?.trim() === "Other") {
            setOtherInspectionMode(
              InspectionModeOutDTO.fromInspectionMode(
                pagedInspectonModes.items[i]!,
                "",
              ),
            );
            break;
          }
        }

        // If this is for an Update operation => Load the partial CondRep model from the Database
        if (cid) {
          const conditionReport = await apiClient?.get<ConditionReportOutDTO>({
            url: `/conditionreports/${cid}`,
          });
          if (conditionReport?.survey_date) {
            conditionReport.survey_date =
              conditionReport.survey_date.split("T")[0];
          }
          if (conditionReport) {
            setSelectedAnnotations(conditionReport.annotations);

            setSelectedAnnexDataObjects(conditionReport.annex_datas);

            setUsersAlreadySharing(conditionReport.users);

            setSelectedCHUsages(conditionReport.use);

            setSelectedInspectionModes(conditionReport.inspection_modes);

            // Fill in the description of the "Other" Inspection Mode (if selected)
            if (
              conditionReport.inspection_modes.some(
                (im) => im.inspection_mode.type?.trim() === "Other",
              )
            ) {
              const otherIM = conditionReport.inspection_modes.find(
                (im) => im.inspection_mode.type?.trim() === "Other",
              );
              if (otherIM) {
                setOtherInspectionModesDescription(otherIM.description);
              }
            }

            // Fill in the description of the "Other" Inspection Mode (if selected)
            if (
              conditionReport.use.some(
                (cu) => cu.ch_usage.name?.trim() === "Other",
              )
            ) {
              const otherCU = conditionReport.use.find(
                (cu) => cu.ch_usage.name?.trim() === "Other",
              );
              if (otherCU) {
                setOtherCHUsageDescription(otherCU.description);
              }
            }
          }

          setFormData(conditionReport || ConditionReportOutDTO.EmptyInstance());

          console.log("=============Cond Report:");
          console.log(conditionReport);
        }
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Failed to load condition report data");
        }
        setLoading(false);
      }
    };
    loadData();
  }, [apiClient, e3did, cid]);

  // Stage 2: When e3D is available, load CH element
  useEffect(() => {
    const loadChelement = async () => {
      if (e3D?.id_chelement != null && e3D.id_chelement > 0) {
        const chel = await apiClient?.get<CHElement>({
          url: APIEndPoints.CHELEMENTS.ONE(e3D.id_chelement),
        });
        setChelement(chel);
        setId_chelement(chel.id!);
        // console.log("Parent CHelement:", chel);
      }
    };

    loadChelement();
  }, [e3D]);

  // Stage 3: When both e3D and chelement are available, prefill the dependent fields from Condition Report (mostly foreign key IDs)
  useEffect(() => {
    const loadCountry = async (country_id: number) => {
      try {
        if (!country_id || country_id <= 0) {
          console.warn("Invalid country_id:", country_id);
          setCountry("");
          return;
        }
        const country = await apiClient?.get<Country>({
          url: APIEndPoints.COUNTRIES.ONE(country_id),
        });
        if (country) {
          setCountry(country.name ?? "");
          // console.log("Country:", country.name);
        } else {
          console.error("No country data received for id:", country_id);
          setCountry("");
        }
      } catch (error) {
        console.error("Error loading country:", error);
        setCountry("");
      }
    };

    const loadProvince = async (province_id: number) => {
      try {
        if (!province_id || province_id <= 0) {
          console.warn("Invalid province_id:", province_id);
          setProvince("");
          return;
        }
        const province = await apiClient?.get<AdministrativeStructure>({
          url: APIEndPoints.PROVINCES.ONE(province_id),
        });
        if (province) {
          setProvince(province.name ?? "");
          console.log("Province:", province.name);
        } else {
          console.error("No province data received for id:", province_id);
          setProvince("");
        }
      } catch (error) {
        console.error("Error loading province:", error);
        setProvince("");
      }
    };

    const loadMunicipality = async (municipality_id: number) => {
      try {
        if (!municipality_id || municipality_id <= 0) {
          console.warn("Invalid municipality_id:", municipality_id);
          setMunicipaliy("");
          return;
        }
        const municipality = await apiClient?.get<AdministrativeStructure>({
          url: APIEndPoints.MUNICIPALITIES.ONE(municipality_id),
        });
        if (municipality) {
          setMunicipaliy(municipality.name ?? "");
          console.log("Municipality:", municipality.name);
        } else {
          console.error(
            "No municipality data received for id:",
            municipality_id,
          );
          setMunicipaliy("");
        }
      } catch (error) {
        console.error("Error loading municipality:", error);
        setMunicipaliy("");
      }
    };

    if (e3D && chelement) {
      const cr = formData;
      cr.id_user = authManager.getUserID()!;
      cr.id_e3dmodel = e3D.id!;
      cr.id_province = chelement.id_province!;
      cr.id_municipality = chelement.id_municipality!;
      cr.address = chelement.address;
      cr.parent_asset_name = e3D.name;
      cr.date = e3D.reference_date;
      cr.author = chelement.author;
      cr.cultural_sphere = chelement.cultural_sphere;

      setFormData(cr);
      setLoading(false);
      setChelementScale(chelement.scale);
      // console.log("+++++++++");
      // console.log(cr);
    }

    if (chelement) {
      if (chelement.id_country) loadCountry(chelement.id_country);
      if (chelement.id_province) loadProvince(chelement.id_province);

      if (chelement.id_municipality)
        loadMunicipality(chelement.id_municipality);
    }
  }, [chelement]);

  // Stage 4: Load all Annotations associated with the parent chelement->e3dmodel->3dinstance
  // Add useLayoutEffect to simulate page load event
  useEffect(() => {
    if (!chelement) return;

    const loadAnnotations = async () => {
      // Feth all e3DModel objects of the current chelement
      try {
        const e3dResponse = await apiClient?.get<PagedData<e3dModel[]>>({
          url: APIEndPoints.E3DS.ALL(chelement.id!),
          requiresToken: true,
        });

        const e3dModels = e3dResponse?.items ?? [];
        // Fetch all instances for each model
        const instancePromises = e3dModels.map((model) =>
          apiClient
            ?.get<PagedData<_3DInstance[]>>({
              url: APIEndPoints._3DINSTANCES.ALL(model.id!),
              requiresToken: true,
            })
            .then((res) => res?.items ?? [])
            .catch(() => []),
        );

        const instancesNested = await Promise.all(instancePromises);
        const allInstances = instancesNested.flat();
        console.log("3DInstances_1: ", allInstances);

        // Fetch all annotations for all instances
        const annotationPromises = allInstances.map((instance) =>
          apiClient
            ?.get<PagedData<Annotation[]>>({
              url: APIEndPoints.ANNOTATIONS.ALL(instance.id!),
              requiresToken: true,
            })
            .then((res) => res?.items ?? [])
            .catch(() => []),
        );

        const annotationsNested = await Promise.all(annotationPromises);
        const allAnnotations = annotationsNested.flat();
        console.log("Annotations: ", allAnnotations);

        setAnnotations(allAnnotations);
      } catch (err) {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      } finally {
        setLoading(false);
      }
    };

    loadAnnotations();
  }, [chelement]);

  // Stage 5: Act on page load - refresh selected Annotation cards and checked ChUsages/Inspection Modes
  // Add useLayoutEffect to simulate page load event
  const isInitialRender = useRef(true);

  useLayoutEffect(() => {
    if (
      isInitialRender.current &&
      annotations.length > 0 &&
      selectedAnnotations.length > 0
    ) {
      refreshAnnotationCards();
      isInitialRender.current = false; // Prevent re-running
    }
  }, [annotations.length, selectedAnnotations]);

  function refreshAnnotationCards() {
    if (selectedAnnotations && selectedAnnotations.length > 0) {
      for (var index in selectedAnnotations) {
        const element = document.querySelector(
          `[id="${selectedAnnotations[index]}"]`,
        );
        if (element) {
          // Set background color to green
          element.classList.toggle("bg-green-100");
        }
        // console.log("Click:" + selectedAnnotations[index]);
      }
    }
  }

  // useEffect(() => {
  //   console.log("selected IMMMMMMMMS:")
  //   console.log(selectedInspectionModes)

  //   console.log("Alllllll CHusages:")
  //   console.log(allCHUsages)

  // }, [selectedInspectionModes]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!e3did && !cid) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "No valid ID provided in the URL.",
      });
      return;
    }

    if (!e3D) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "No valid E3DModel parent object detected!",
      });
      return;
    }

    if (!chelement) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "No valid CHElement parent object detected!",
      });
      return;
    }

    // var array_inspection_modes =  Array.from(selectedInspectionModes).map(im => ({ description: im.description ?? "", presence: im.presence, type: im.type, inspection_mode_id: im.id!}));

    // var array_ch_users =  Array.from(selectedCHUsages).map(ch => ({ description: ch.description ?? "", ch_usage_id: ch.id!}));

    var references = selectedAnnexDataObjects
      .map((a) => a.id)
      .filter((id): id is number => id !== undefined)
      .join(",");
    var id_annex_datas = selectedAnnexDataObjects
      .map((a) => a.id)
      .filter((id): id is number => id !== undefined);

    if (
      selectedInspectionModes.some(
        (im) => im.inspection_mode.type?.trim() === "Other",
      )
    ) {
      const otherIM = selectedInspectionModes.find(
        (im) => im.inspection_mode.type?.trim() === "Other",
      );
      if (otherIM) {
        otherIM.description = otherInspectionModesDescription;
      }
    }

    if (selectedCHUsages.some((cu) => cu.ch_usage.name?.trim() === "Other")) {
      const otherCU = selectedCHUsages.find(
        (cu) => cu.ch_usage.name?.trim() === "Other",
      );
      if (otherCU) {
        otherCU.description = otherCHUsageDescription;
      }
    }

    // Example function where you build the DTO
    const cr = new ConditionReportOutDTO(
      formData.name, // name
      formData.description, // description
      0, // id (new report → 0 or undefined)
      authManager.getUserID()!, // id_user
      parseInt(e3did!), // id_e3dmodel (set if available)
      "", // GUID
      undefined, // created_on
      selectedCHUsages, // usages: CHUsageOutDTO[]
      selectedInspectionModes, // inspection_modes: InspectionModeOutDTO[]
      selectedAnnotations, // annotations (if any selected)
      selectedAnnexDataObjects, // annex data (if any selected)
      usersAlreadySharing, // users: UserSummaryOutDTO[]
      id_annex_datas,
      {
        id_annotations: formData.id_annotations,
        id_province: formData.id_province,
        id_municipality: formData.id_municipality,
        address: formData.address,
        date: formData.date,
        author: formData.author,
        cultural_sphere: formData.cultural_sphere,
        legal_status: formData.legal_status,
        conservation_history: formData.conservation_history,
        reference_cartography_municipality:
          formData.reference_cartography_municipality,
        reference_cartography_cadastral_sheet:
          formData.reference_cartography_cadastral_sheet,
        reference_cartography_cadastral_parcel:
          formData.reference_cartography_cadastral_parcel,
        reference_cartography_other: formData.reference_cartography_other,
        parent_asset_name: formData.parent_asset_name,
        specific_location: formData.specific_location,
        colocation_characteristics: formData.colocation_characteristics,
        techniques: formData.techniques,
        object: formData.object,
        subject: formData.subject,
        diagnosis_description: formData.diagnosis_description,
        condition_recommendations: formData.condition_recommendations,
        state_of_conservation: formData.state_of_conservation,
        environmental_conditions_temperature:
          formData.environmental_conditions_temperature,
        environmental_conditions_relative_humidity:
          formData.environmental_conditions_relative_humidity,
        environmental_conditions_current_weather_condition:
          formData.environmental_conditions_current_weather_condition,
        environmental_conditions_past_week_weather_condition:
          formData.environmental_conditions_past_week_weather_condition,
        survey_date: formData.survey_date,
        report_references: references,
        status: formData.status,
        survey_responsible: formData.survey_responsible,
      },
    );

    console.log("##############################");
    console.log(cr);

    var crIn = ConditionReportInDTO.FromOutDTO(cr);

    if (!cid) {
      try {
        crIn.status = DocumentState.draft;
        console.log("Creating new Condition Report");
        console.log(crIn);
        // print json object version of cr variable
        // console.log(JSON.stringify(cr, null, 2));
        await apiClient?.post_timeout({
          url: APIEndPoints.CONDITION_REPORTS.NEW(parseInt(e3did!, 10)),
          body: crIn,
          timeout: 60000, // 60 seconds timeout
        });
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Condition Report created successfully",
        });

        navigate(
          SETTINGS.get_path({
            section: AppSections.E3DS,
            id: parseInt(e3did!),
            additionalSections: [],
            subsection: AppSubSections.VIEW,
          }),
        );
      } catch (error: any) {
        console.error("Error creating Condition Report:", error);
        if (error.code === "ECONNABORTED") {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message:
              "Request timed out. The server took too long to respond. Please try again later.",
          });
        } else if (!error.processed) {
          processAPIErrors(error);
        }
        return;
      }
    } else {
      // Update existing record
      cr.id = parseInt(cid);
      try {
        if (editCopy) {
          // formData.name = crIn.name + "_Copy";
          // crIn.name = crIn.name + "_Copy";

          // Copy existing record
          await apiClient?.post_timeout({
            url: APIEndPoints.CONDITION_REPORTS.NEW(parseInt(e3did!, 10)),
            body: crIn,
            timeout: 60000, // 60 seconds timeout
          });
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Condition Report created successfully",
          });

          navigate(
            SETTINGS.get_path({
              section: AppSections.E3DS,
              id: parseInt(e3did!),
              additionalSections: [],
              subsection: AppSubSections.VIEW,
            }),
          );
        } else {
          // Update existing record
          await apiClient?.put({
            url: APIEndPoints.CONDITION_REPORTS.ONE(parseInt(cid)),
            body: crIn,
          });
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Condition Report updated successfully",
          });

          navigate(
            SETTINGS.go_back({
              section: AppSections.E3DS,
              id: parseInt(e3did!),
              additionalSections: [],
              subsection: AppSubSections.VIEW,
            }),
          );
        }
      } catch (error: any) {
        if (!error.processed) {
          processAPIErrors(error);
        }
        return;
      }
    }
  };

  const formInputChange = (inputID: string, newValue: string) => {
    // if the change is detected on "other_chusage_description"
    if (inputID === "other_chusage_description") {
      setOtherCHUsageDescription(newValue);
      let other = otherCHUsage;
      if (other) {
        other.description = newValue;
        setOtherCHUsage(other);
      }
    }

    if (inputID === "other_inspectionmode_description") {
      setOtherInspectionModesDescription(newValue);
      let other = otherInspectionMode;
      if (other) {
        other.description = newValue;
        setOtherInspectionMode(other);
      }
    }

    setFormData(
      (prev) =>
        new ConditionReportOutDTO(
          inputID === "cr-name" ? newValue : prev.name,
          inputID === "cr-description" ? newValue : prev.description,
          prev.id ?? 0,
          prev.id_user,
          prev.id_e3dmodel,
          prev.GUID ?? "",
          prev.created_on,
          prev.use,
          prev.inspection_modes,
          prev.annotations,
          prev.annex_datas,
          prev.users,
          prev.id_annex_datas,
          {
            id_annotations: prev.id_annotations,
            id_province: prev.id_province,
            id_municipality: prev.id_municipality,
            address: inputID === "cr-address" ? newValue : prev.address,
            date: inputID === "cr-date" ? newValue : prev.date,
            author: inputID === "cr-author" ? newValue : prev.author,
            cultural_sphere:
              inputID === "cr-cultural-sphere"
                ? newValue
                : prev.cultural_sphere,
            legal_status:
              inputID === "cr-legal-status" ? newValue : prev.legal_status,
            conservation_history:
              inputID === "cr-conservation-history"
                ? newValue
                : prev.conservation_history,
            reference_cartography_municipality:
              inputID === "cr-reference-cartography-municipality"
                ? newValue
                : prev.reference_cartography_municipality,
            reference_cartography_cadastral_sheet:
              inputID === "cr-reference-cartography-cadastral-sheet"
                ? newValue
                : prev.reference_cartography_cadastral_sheet,
            reference_cartography_cadastral_parcel:
              inputID === "cr-reference-cartography-cadastral-parcel"
                ? newValue
                : prev.reference_cartography_cadastral_parcel,
            reference_cartography_other:
              inputID === "cr-reference-cartography-other"
                ? newValue
                : prev.reference_cartography_other,
            parent_asset_name: prev.parent_asset_name,
            specific_location:
              inputID === "cr-specific-location"
                ? newValue
                : prev.specific_location,
            colocation_characteristics:
              inputID === "cr-colocation-characteristics"
                ? newValue
                : prev.colocation_characteristics,
            techniques:
              inputID === "cr-techniques" ? newValue : prev.techniques,
            object: inputID === "cr-object" ? newValue : prev.object,
            subject: inputID === "cr-subject" ? newValue : prev.subject,
            diagnosis_description:
              inputID === "cr-diagnosis-description"
                ? newValue
                : prev.diagnosis_description,
            condition_recommendations:
              inputID === "cr-condition-recommendations"
                ? newValue
                : prev.condition_recommendations,
            state_of_conservation:
              inputID === "cr-state-of-conservation"
                ? (newValue as ConservationState)
                : prev.state_of_conservation,
            environmental_conditions_temperature:
              inputID === "cr-environmental-conditions-temperature"
                ? newValue
                : prev.environmental_conditions_temperature,
            environmental_conditions_relative_humidity:
              inputID === "cr-environmental-conditions-relative-humidity"
                ? newValue
                : prev.environmental_conditions_relative_humidity,
            environmental_conditions_current_weather_condition:
              inputID ===
              "cr-environmental-conditions-current-weather-condition"
                ? newValue
                : prev.environmental_conditions_current_weather_condition,
            environmental_conditions_past_week_weather_condition:
              inputID ===
              "cr-environmental-conditions-past-week-weather-condition"
                ? newValue
                : prev.environmental_conditions_past_week_weather_condition,
            survey_date:
              inputID === "cr-survey-date" ? newValue : prev.survey_date,
            report_references:
              inputID === "cr-report-references"
                ? newValue
                : prev.report_references,
            status:
              inputID === "cr-status"
                ? (newValue as DocumentState)
                : prev.status,
            survey_responsible:
              inputID === "cr-survey-responsible"
                ? newValue
                : prev.survey_responsible,
          },
        ),
    );
  };

  const onFormCancel = () => {
    navigate(
      SETTINGS.go_back({
        section: AppSections.E3DS,
        id: parseInt(e3did!),
        additionalSections: [],
        subsection: AppSubSections.VIEW,
      }),
    );
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = { ...defaultFormErrors };
      const tabErrorMap: Partial<Record<TabKey, IApiErrorSummaryItem>> = {};

      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
        const mapping = FIELD_TAB_MAP[apiError.loc[0]];
        if (mapping) {
          if (!tabErrorMap[mapping.tab]) {
            tabErrorMap[mapping.tab] = {
              tab: mapping.tab,
              tabLabel: TAB_LABELS[mapping.tab],
              fields: [],
            };
          }
          tabErrorMap[mapping.tab]!.fields.push(mapping.label);
        }
      });

      setFormErrors(formErrorsCopy);
      setApiErrorSummary(Object.values(tabErrorMap) as IApiErrorSummaryItem[]);
      setErrorMessage("Some fields are invalid. Go to:");
    } catch (exception) {
      console.log(exception);
      setApiErrorSummary([]);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = cid
    ? "Edit Condition Report"
    : "Create a new Condition Report";
  const confirmLabel = editCopy
    ? "Create Copy Report"
    : cid
      ? "Update Condition Report"
      : "Create Condition Report";

  const deleteButton = cid ? (
    <Button
      label="Delete Condition Report"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this condition report?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete report",
          onConfirm: () => {
            apiClient
              ?.delete({ url: `/conditionreports/${cid}` })
              .then(() => {
                alertsManager.showAlertMessage({
                  type: AlertMessageType.SUCCESS,
                  message: "Condition Report deleted successfully",
                });
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.E3DS,
                    id: parseInt(e3did!),
                    additionalSections: [],
                    subsection: AppSubSections.VIEW,
                  }),
                );
              })
              .catch((error: IApiClientError) => {
                if (!error.processed) {
                  alertsManager.showAlertMessage({
                    type: AlertMessageType.ERROR,
                    message: "Failed to delete condition report",
                  });
                }
              });
          },
        });
      }}
    />
  ) : null;

  const conservation_state_options = Object.entries(ConservationState).map(
    ([key, value]) => ({
      label: key,
      value: value,
    }),
  );

  const current_use_options = Object.entries(UsageTypes).map(
    ([key, value]) => ({
      label: key,
      value: value,
    }),
  );

  const changeReportState = (state: DocumentState) => {
    let message_public =
      "Are you sure you want to make this condition report public?";
    let confirmation_message_public = "Yes, make public!";
    let message_finished =
      "Are you sure you want to mark this condition report as finished?";
    let confirmation_message_finished = "Yes, mark as finished!";

    let selected_prompt = message_public;
    let confirmation_prompt = confirmation_message_public;
    if (state == DocumentState.finished) {
      selected_prompt = message_finished;
      confirmation_prompt = confirmation_message_finished;
    }

    alertsManager.showDialogWindow({
      type: DialogWindowType.CONFIRM,
      message: selected_prompt,
      cancelLabel: "Cancel",
      confirmLabel: confirmation_prompt,
      onConfirm: () => {
        try {
          let prev = formData;
          prev.status = state;
          setFormData(prev);

          //Update object in database
          apiClient?.put({
            url: APIEndPoints.CONDITION_REPORTS.ONE(parseInt(cid!)),
            body: formData,
          });

          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Report status changed to public",
          });

          navigate(
            SETTINGS.get_path({
              section: AppSections.E3DS,
              id: parseInt(e3did!),
              additionalSections: [],
              subsection: AppSubSections.VIEW,
            }),
          );
        } catch (error: any) {
          if (!error.processed) {
            processAPIErrors(error);
          }
          return;
        }
      },
    });
  };

  function onClickAnnotationCard(id: number | undefined): void {
    if (id === undefined) return;

    setSelectedAnnotations((prev) => {
      const alreadySelected = prev.some((a) => a.id === id);
      if (alreadySelected) {
        return prev.filter((a) => a.id !== id); // remove
      } else {
        const annotation = annotations.find((a) => a.id === id);
        return annotation ? [...prev, annotation] : prev; // add
      }
    });
  }

  const checkBoxListChange = (id: string, checked: boolean) => {
    const element_type = id.split("_")[0];
    const element_id = Number(id.split("_")[1]);
    console.log(id);
    console.log(
      "###################################" +
        element_id +
        ":" +
        element_type +
        ":" +
        checked,
    );
    if (element_type === "chusage") {
      // Find the CHUsage with this id inside allCHUsages
      const chUsage = allCHUsages.find(
        (chusage) => chusage.ch_usage.id === element_id,
      );

      // Create a new Set to avoid mutating the state directly
      const copyCHUsages = new Set(selectedCHUsages);

      if (checked) {
        // Only add if not already present (based on ch_usage_id)
        const existing = Array.from(copyCHUsages).find(
          (c) => c.ch_usage.id === element_id,
        );
        if (!existing) {
          copyCHUsages.add(chUsage!);
        }
      } else {
        // Remove the specific chUsage based on ch_usage_id
        copyCHUsages.forEach((ch) => {
          if (ch.ch_usage.id === element_id) {
            copyCHUsages.delete(ch);
          }
        });
      }
      console.log("clicked: " + id + " ----- " + checked);
      console.log("Updated copyCHUsages:", copyCHUsages);
      setSelectedCHUsages(Array.from(copyCHUsages));
    } else if (element_type === "inspectionmode") {
      // Find the CHUsage with this id inside allCHUsages
      const inspection_mode = allInspectionModes.find(
        (insp_mode) => insp_mode.inspection_mode.id === element_id,
      );

      // Create a new Set to avoid mutating the state directly
      const copyInspectionModes = new Set(selectedInspectionModes);

      if (checked) {
        // Only add if not already present (based on ch_usage_id)
        const existing = Array.from(copyInspectionModes).find(
          (insp_mode) => insp_mode.inspection_mode.id === element_id,
        );
        if (!existing && inspection_mode) {
          copyInspectionModes.add(inspection_mode);
        }
        console.log("Checked");
      } else {
        // Remove the specific chUsage based on ch_usage_id
        copyInspectionModes.forEach((insp_mode) => {
          if (insp_mode.inspection_mode.id === element_id) {
            copyInspectionModes.delete(insp_mode);
          }
        });
        console.log("UNChecked");
      }
      console.log("clicked: " + id + " ----- " + checked);
      console.log("Updated inspectionModes:", copyInspectionModes);
      setSelectedInspectionModes(Array.from(copyInspectionModes));
    }
  };

  const selectedCHUsagesSet = new Set(selectedCHUsages);
  const selectedInspectionModesSet = new Set(selectedInspectionModes);
  const selectedMaterialsIDsSet = new Set(selectedMaterialsIDs);

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(event) => {
        handleSubmit(event).catch((error) => {
          console.error("Error during form submission:", error);
        });
      }}
      headButton={
        <>
          {editCopy === false && formData.status === DocumentState.draft && (
            <Button
              label="Mark as Finished"
              purpose={ButtonAction.EDIT}
              onClick={() => changeReportState(DocumentState.finished)}
            />
          )}
          {editCopy === false && formData.status === DocumentState.finished && (
            <Button
              label="Make Public"
              purpose={ButtonAction.EDIT}
              onClick={() => changeReportState(DocumentState.public)}
            />
          )}
        </>
      }
    >
      {/*================================================= TAB Buttons ==========================================================*/}
      <div className="flex border-b mb-4">
        <button
          className={`px-4 py-2 -mb-px ${
            activeTab === "tab1"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={(e) => {
            e.preventDefault(); // Prevent any default navigation
            e.stopPropagation(); // Stop event from bubbling up to parent navigators
            setActiveTab("tab1");
          }}
        >
          General Information
        </button>

        <button
          className={`px-4 py-2 -mb-px ${
            activeTab === "tab2"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab("tab2");
          }}
        >
          Description
        </button>

        <button
          className={`px-4 py-2 -mb-px ${
            activeTab === "tab3"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab("tab3");
          }}
        >
          Diagnosis
        </button>

        <button
          className={`px-4 py-2 -mb-px ${
            activeTab === "tab4"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab("tab4");
          }}
        >
          Survey Information
        </button>
        <div className="grow"></div>
        <button
          className={`px-4 py-2 -mb-px ${
            activeTab === "tab5"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab("tab5");
          }}
        >
          {AppIcons.USER}
          Contributors
        </button>
      </div>

      {/* GENERAL INFORMATION TAB */}
      {activeTab === "tab1" && (
        <>
          {/* BOTH */}
          {/* Name of Cond Rep */}
          <FormInputText
            label="Condition Report Name:"
            id="cr-name"
            value={formData.name}
            required={true}
            placeholder="Name"
            onChange={formInputChange}
            error={formErrors.name}
          />

          {/* Condition Report Date: */}
          <FormInputDate
            label="Condition Report Date:"
            id="cr-date"
            value={formData.date}
            required={false}
            onChange={formInputChange}
            error={formErrors.date}
          />

          {/* BOTH */}
          {/* Country */}
          <FormInputText
            label="Country:"
            id="cr-country"
            value={country}
            disabled={true} // disables editing
            onChange={formInputChange}
          />

          {/* BOTH */}
          {/* Province */}
          <FormInputText
            label="Province:"
            id="cr-province"
            value={province}
            disabled={true} // disables editing
            onChange={formInputChange}
          />

          {/* BOTH */}
          {/* Municipality */}
          <FormInputText
            label="Municipality:"
            id="cr-municipality"
            value={municipality}
            disabled={true} // disables editing
            onChange={formInputChange}
          />

          {/* BOTH */}
          {/* Address */}
          <FormInputText
            label="Address:"
            id="cr-address"
            value={formData.address}
            required={false}
            placeholder="Address"
            onChange={formInputChange}
            error={formErrors.address}
          />

          {/* OBJECT */}
          {/* Specific location */}
          {chelementScale === CHElementScale.Object && (
            <FormRichTextEditor
              label="Specific Location:"
              id="cr-specific-location"
              value={formData.specific_location}
              required={false}
              // placeholder="Specific Location"
              onChange={formInputChange}
              error={formErrors.specific_location}
              className="quill-editor-small"
              hint="Refers to areas such as the apse, transept, room number, corridor, gallery, patio, courtyard, etc."
            />
          )}

          {/* OBJECT */}
          {chelementScale === CHElementScale.Object && (
            <FormRichTextEditor
              label="Colocation Characteristics:"
              id="cr-colocation-characteristics"
              value={formData.colocation_characteristics}
              required={false}
              onChange={formInputChange}
              error={formErrors.colocation_characteristics}
              className="quill-editor"
              hint="Refers to conditions such as indoor, outdoor, semi-indoor, showcase, on panel, protection system, specific adjacent environments/rooms, etc."
            />
          )}


          {/* BOTH */}
          {/* Author */}
          <FormInputText
            label={
              chelementScale === CHElementScale.Object ? "Asset Author" : "Asset Creator"
            }
            id="cr-author"
            value={formData.author}
            required={false}
            placeholder={
              chelementScale === CHElementScale.Object ? "Author of the CH Asset" : "Creator of the CH Asset"
            }
            onChange={formInputChange}
            error={formErrors.author}
          />

          {/* BOTH */}
          {/* Cultural Sphere */}
          <FormRichTextEditor
            label="Cultural Sphere:"
            id="cr-cultural-sphere"
            value={formData.cultural_sphere}
            required={false}
            onChange={formInputChange}
            error={formErrors.cultural_sphere}
            className="quill-editor"
            hint="Refers to the broader cultural, historical, artistic, and social context to which a particular cultural heritage asset belongs."
          />

          {/* BOTH */}
          {/* Legal Status */}
          <FormRichTextEditor
            label="Legal Status:"
            id="cr-legal-status"
            value={formData.legal_status}
            required={false}
            onChange={formInputChange}
            error={formErrors.legal_status}
            className="quill-editor"
            hint="Refers to the legal protections, ownership rights, and usage limitations established by national and international laws."
          />

          {/* BUILDING */}
          {/* Cartography */}
          {chelementScale === CHElementScale.Building && (
            <>
              <label className="form-label">Reference Cartography</label>
              <div className="form-input-group-vertical">
                <FormRichTextEditor
                  label="Municipality:"
                  id="cr-reference-cartography-municipality"
                  value={formData.reference_cartography_municipality}
                  required={false}
                  // placeholder="Municipality"
                  onChange={formInputChange}
                  error={formErrors.reference_cartography_municipality}
                  className="quill-editor-small"
                  hint="Official registry identifiers used to uniquely locate and describe the property within the national land registry system."
                />

                <FormRichTextEditor
                  label="Cadastral Sheet:"
                  id="cr-reference-cartography-cadastral-sheet"
                  value={formData.reference_cartography_cadastral_sheet}
                  required={false}
                  // placeholder="Cadastral Sheet"
                  onChange={formInputChange}
                  error={formErrors.reference_cartography_cadastral_sheet}
                  className="quill-editor-small"
                  hint="Official registry identifiers used to uniquely locate and describe the property within the national land registry system."
                />
                <FormRichTextEditor
                  label="Cadastral Parcel:"
                  id="cr-reference-cartography-cadastral-parcel"
                  value={formData.reference_cartography_cadastral_parcel}
                  required={false}
                  // placeholder="Cadastral Parcel"
                  onChange={formInputChange}
                  error={formErrors.reference_cartography_cadastral_parcel}
                  className="quill-editor-small"
                  hint="Official registry identifiers used to uniquely locate and describe the property within the national land registry system."
                />
                <FormRichTextEditor
                  label="Other:"
                  id="cr-reference-cartography-other"
                  value={formData.reference_cartography_other}
                  required={false}
                  onChange={formInputChange}
                  error={formErrors.reference_cartography_other}
                  className="quill-editor-small"
                  hint="Official registry identifiers used to uniquely locate and describe the property within the national land registry system."
                />
              </div>
            </>
          )}

          {/* BOTH */}
          {/* Conservation History */}
          <FormRichTextEditor
            label="Conservation history and past interventions:"
            id="cr-conservation-history"
            value={formData.conservation_history}
            required={false}
            onChange={formInputChange}
            error={formErrors.conservation_history}
            className="quill-editor"
          />
        </>
      )}

      {/* DESCRIPTION TAB */}
      {activeTab === "tab2" && (
        <>
          {/* Horizontal separator  */}
          {/* <hr className="my-4 border-t-2 border-gray-300" /> */}

          {/* BOTH */}
          {/* General Description */}
          <FormRichTextEditor
            label="General description:"
            id="cr-description"
            value={formData.description}
            required={false}
            onChange={formInputChange}
            error={formErrors.description}
            className="quill-editor"
          />

          {/* OBJECT */}
          {chelementScale === CHElementScale.Object && (
            <>
              {/* Object */}
              <FormRichTextEditor
                label="Object:"
                id="cr-object"
                value={formData.object}
                required={false}
                onChange={formInputChange}
                error={formErrors.object}
                className="quill-editor-small"
                hint="Refers to the type of cultural heritage asset such as paintings, sculptures, or antique prints and whether the object is a unique piece or part of a collection."
              />

              {/* Subject */}
              <FormRichTextEditor
                label="Subject:"
                id="cr-subject"
                value={formData.subject}
                required={false}
                onChange={formInputChange}
                error={formErrors.subject}
                className="quill-editor-small"
                hint="Refers to the theme or subject of the cultural heritage asset such as historical events, religious iconography, portraits, etc."
              />
            </>
          )}

          {/* BOTH */}
          {/* Dimensions - TO DO */}
          {/* <FormInputText
                    label="Dimensions:"
                    id="cr-dimensions"
                    value={formData.dimensions}
                    placeholder="Dimensions"
                    onChange={formInputChange}
                /> */}

          {/* BOTH */}
          {/* Materials */}
          {/* <label className="form-label">Select materials</label>
                <div className="form-input-group" style={{ marginTop: "5px" }}>
                  {allMaterials.map((material) => {
                    return (
                      <FormInputCheckbox
                        key={"ficb_" + material.id}
                        label={""}
                        details={Material.Display(material)}
                        id={"material_" + material.id}
                        checked={selectedMaterialsIDsSet.has(material.id!)}
                        required={false}
                        onChange={checkBoxListChange}
                        css_label="form-label-inline"
                      />
                    );
                  })}

                  {selectedMaterialsIDsSet.has(otherMaterialID!) && (
                    <div className="col-span-2 mx-6 mt-2">
                      <FormInputText
                        label="Details on other materials"
                        id="other_material_description"
                        value={otherMaterialDescription}
                        required={false}
                        placeholder=""
                        onChange={formInputChange}
                      />
                    </div>
                  )}
                </div> */}

          {/* BOTH */}
          {/* Techniques */}
          <FormRichTextEditor
            label="Techniques:"
            id="cr-techniques"
            value={formData.techniques}
            required={false}
            onChange={formInputChange}
            error={formErrors.techniques}
            className="quill-editor"
          />

          {/* BUILDING */}
          {chelementScale === CHElementScale.Building && (
            <>
              {/*  USE */}
              {/* Horizontal separator */}
              <hr className="my-4 border-t-2 border-gray-300" />

              <label className="form-label">Select usages</label>
              <div className="form-input-group" style={{ marginTop: "5px" }}>
                {allCHUsages.map((chusage) => {
                  return (
                    <FormInputCheckbox
                      label={""}
                      details={CHUsageOutDTO.Display(chusage)}
                      // details={chusage.name}
                      id={"chusage_" + chusage.ch_usage.id}
                      // checked={Array.from(selectedCHUsages).some((sel) => (sel as any).ch_usage?.id === chusage.id)}
                      checked={
                        selectedCHUsages &&
                        Array.from(selectedCHUsages).some(
                          (sel) => sel.ch_usage.id === chusage.ch_usage.id,
                        )
                      }
                      required={false}
                      onChange={checkBoxListChange}
                      css_label="form-label-inline"
                    />
                  );
                })}
                {/* has(otherCHUsage!) */}
                {selectedCHUsages &&
                  selectedCHUsages!.some(
                    (sel) => sel.ch_usage.id === otherCHUsage?.ch_usage.id,
                  ) && (
                    <div className="col-span-2 mx-6 mt-2">
                      <FormInputText
                        label="Details on other uses"
                        id="other_chusage_description"
                        value={otherCHUsageDescription}
                        required={false}
                        placeholder=""
                        onChange={formInputChange}
                      />
                    </div>
                  )}
              </div>
            </>
          )}
        </>
      )}

      {/* DIAGNOSIS TAB */}
      {activeTab === "tab3" && (
        <>
          <ConditionReportAddEdit_annotations
            formInputChange={formInputChange}
            activeTab={activeTab}
            selectedAnnotations={selectedAnnotations}
            annotations={annotations}
            onClickAnnotationCard={onClickAnnotationCard}
            hint={"Select the annotations that contain the desired alterations information."}
          />

          {/* BOTH */}
          {/* Diagnosis Description */}
          <FormRichTextEditor
            label="Diagnosis Description:"
            id="cr-diagnosis-description"
            value={formData.diagnosis_description}
            required={false}
            onChange={formInputChange}
            error={formErrors.diagnosis_description}
            className="quill-editor"
            hint="Expose and analyse the condition of the cultural heritage asset. Provide a comprehensive and detailed diagnosis of the state of conservation based on what has been observed and documented. Describe the damage in a structured manner, for example, starting from the structural elements, followed by architectural features, and finishing with surface conditions."
          />

          {/* BOTH */}
          {/* State of Conservation */}
          <FormDropDown
            label="State of conservation survey:"
            id="cr-state-of-conservation"
            value={formData.state_of_conservation}
            required={false}
            options={conservation_state_options}
            onChange={(id, value) => formInputChange(id, value)}
            error={formErrors.state_of_conservation}
          />
        </>
      )}

      {/* SURVEY INFORMATION TAB   */}
      {activeTab === "tab4" && (
        <>
          {/* BOTH */}
          {/* INSPECTION MODE => TO DO */}
          <label className="form-label">Select inspection modes</label>
          <div
            className="form-input-group grid-cols-1"
            style={{ marginTop: "5px" }}
          >
            {
              // remove the option "Support and/or backside inspection(On-site)" from the list of Inspection Modes for BUILDING
              allInspectionModes
                .filter(
                  (im) =>
                    im.inspection_mode &&
                    !im.inspection_mode.type?.includes("Support"),
                )
                .map((insp_mode) => {
                  return (
                    <FormInputCheckbox
                      label={""}
                      details={InspectionModeOutDTO.Display(insp_mode)}
                      id={"inspectionmode_" + insp_mode.inspection_mode.id}
                      checked={
                        selectedInspectionModes &&
                        Array.from(selectedInspectionModes).some(
                          (sel) =>
                            sel.inspection_mode.id ===
                            insp_mode.inspection_mode.id,
                        )
                      }
                      required={false}
                      onChange={checkBoxListChange}
                      css_label="form-label-inline"
                    />
                  );
                })
            }

            {selectedInspectionModes &&
              selectedInspectionModes.some(
                (sel) =>
                  sel.inspection_mode.id ===
                  otherInspectionMode?.inspection_mode.id,
              ) && (
                <div className="col-span-1 mx-6 mt-2">
                  <FormInputText
                    label="Details on other inspection modes"
                    id="other_inspectionmode_description"
                    value={otherInspectionModesDescription}
                    required={false}
                    placeholder=""
                    onChange={formInputChange}
                  />
                </div>
              )}
          </div>

          {/* <div className="form-input-group" style={{ marginTop: "5px" }}>
        {allMaterials.map((material) => {
          return (
            <FormInputCheckbox
              key={"ficb_" + material.id}
              label={""}
              details={Material.Display(material)}
              id={"material_" + material.id}
              checked={selectedMaterialsIDsSet.has(material.id!)}
              required={false}
              onChange={checkBoxListChange}
              css_label="form-label-inline"
            />
          );
        })} */}
          {/* 
        {selectedMaterialsIDsSet.has(otherMaterialID!) && (
          <div className="col-span-2 mx-6 mt-2">
            <FormInputText
              label="Details on other materials"
              id="other_material_description"
              value={otherMaterialDescription}
              required={false}
              placeholder=""
              onChange={formInputChange}
            />
          </div>
        )}
      </div> */}

          {/* BOTH */}
          {/*  Environmental survey condition  */}
          <label className="form-label">Environmental Conditions</label>
          <div className="form-input-group-vertical">
            <FormInputText
              label="Temperature:"
              id="cr-environmental-conditions-temperature"
              value={formData.environmental_conditions_temperature}
              required={false}
              // placeholder="Temperature"
              onChange={formInputChange}
              // className="quill-editor-small"
              error={formErrors.environmental_conditions_temperature}
            />
            <FormInputText
              label="Relative Humidity:"
              id="cr-environmental-conditions-relative-humidity"
              value={formData.environmental_conditions_relative_humidity}
              required={false}
              // placeholder="Relative Humidity"
              onChange={formInputChange}
              // className="quill-editor-small"
              error={formErrors.environmental_conditions_relative_humidity}
            />
            <FormRichTextEditor
              label="Current Weather:"
              id="cr-environmental-conditions-current-weather-condition"
              value={
                formData.environmental_conditions_current_weather_condition
              }
              required={false}
              // placeholder="Current Weather Condition"
              onChange={formInputChange}
              className="quill-editor-small"
              error={
                formErrors.environmental_conditions_current_weather_condition
              }
            />
            <FormRichTextEditor
              label="Past Week Weather:"
              id="cr-environmental-conditions-past-week-weather-condition"
              value={
                formData.environmental_conditions_past_week_weather_condition
              }
              required={false}
              // placeholder="Past Week Weather Condition"
              onChange={formInputChange}
              className="quill-editor-small"
              error={
                formErrors.environmental_conditions_past_week_weather_condition
              }
            />
          </div>

          {/* BOTH */}
          {/* Survey Date */}
          <FormInputDate
            label="Survey Date:"
            id="cr-survey-date"
            value={formData.survey_date}
            required={true}
            onChange={formInputChange}
            error={formErrors.survey_date}
          />

          {/* BOTH */}
          {/* Survey Responsible */}
          <FormInputText
            label="Survey Responsible:"
            id="cr-survey-responsible"
            value={formData.survey_responsible}
            required={false}
            placeholder="Survey Responsible"
            onChange={formInputChange}
            error={formErrors.survey_responsible}
          />

          {/* BOTH */}
          {/* Report References */}
          <ConditionReportAddEdit_references
            id_chelement={id_chelement}
            id_e3d={parseInt(e3did!)}
            activeTab={activeTab}
            selectedReferences={selectedAnnexDataObjects}
            onSetSelectedReferences={setSelectedAnnexDataObjects}
            hint="Refers to any documents or images relevant to the current condition report."
          />

          {/* BOTH */}
          {/* CONDITION RCCOMMENDATION */}
          <FormRichTextEditor
            label="General conservation recommendations:"
            id="cr-condition-recommendations"
            value={formData.condition_recommendations}
            required={false}
            onChange={formInputChange}
            error={formErrors.condition_recommendation}
            className="quill-editor"
            hint="This is meant for the conservation experts' observation/recommendations pertinent to this Condition Report"
          />
        </>
      )}

      {/* CONTRIBUTORS TAB */}
      {activeTab === "tab5" && (
        <ConditionReportAddEdit_contributors
          data={formData}
          alreadySharingUsersIDs={usersAlreadySharing}
          setSharingUsers={setUsersAlreadySharing}
          author_id={formData.id_user}
        />
      )}

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>
            <span className="block text-left">{errorMessage}</span>
            {apiErrorSummary.length > 0 && (
              <ul className="mt-1 list-none text-left">
                {apiErrorSummary.map((item) => (
                  <li key={item.tab}>
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab(item.tab);
                      }}
                    >
                      {item.tabLabel}
                    </button>
                    {" — "}
                    {item.fields.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Horizontal separator */}
      <hr className="my-4 border-t-2 border-gray-300" />

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

export default ConditionReportAddEdit;
